import { ACTORS, IDS, KESTREL, LEVELS, ONTOLOGY, P, TEAMS, propertyById } from './kestrelOntology';
import { PACK_CREATED_AT, PACK_MANUFACTURED, PACK_SERIAL, packValues } from './kestrelPassport';
import { snowflakeAt } from './time';

/*
 * The EVP-750 workflows around the pack passport: the supplier data request
 * that brought in its recycled content, the spreadsheet import that created
 * the batch's packs and cells, and a Lab query across them. Each is shaped
 * like the payload its screen receives; the builders that carry a time take
 * a clock (fixtures/time.js createClock).
 */

// ─── Supplier data request ─────────────────────────────────────────────────

/** What the supplier was asked for, in the request's order. */
export const REQUESTED_KEYS = ['recycledCobalt', 'recycledLithium', 'recycledNickel'];

/** A definition as the request snapshots it: the ontology's, minus layout-only keys. */
const definitionOf = (key) => {
    const { id, name, type, value } = propertyById(P[key]);

    return { id, name, type, value };
};

/**
 * The request as the customer (Kestrel) reviews it, in the shape the data
 * request page receives (DataRequestService::represent() plus `viewer_role`
 * and `abilities`):
 *
 * - `accepted`: how many values, from the top, the reviewer has accepted.
 * - `applied`: whether the accepted values have been written to the pack,
 *   which closes the request as `applied`.
 *
 * The defaults are the moment the answer arrives: submitted, nothing decided.
 */
export function supplierRequest(clock, { accepted = 0, applied = false } = {}) {
    const values = packValues();
    const created = clock.ago({ days: 2, hours: 5 });
    const submitted = clock.ago({ hours: 3 });
    const reviewed = clock.ago({ minutes: 2 });
    const appliedAt = clock.ago({ minutes: 1 });
    const acceptedCount = applied ? REQUESTED_KEYS.length : Math.max(0, Math.min(accepted, REQUESTED_KEYS.length));

    const entries = REQUESTED_KEYS.map((key, position) => {
        const isAccepted = position < acceptedCount;

        return {
            property_id: P[key],
            position,
            definition: definitionOf(key),
            submitted_value: String(values[key]),
            coerced_value: values[key],
            review_status: isAccepted ? 'accepted' : 'pending',
            review_comment: null,
            reviewed_at: isAccepted ? reviewed.iso : null,
            source: null,
            applied_at: applied ? appliedAt.iso : null,
            current_value: applied ? values[key] : null,
        };
    });

    return {
        object: 'data_request',
        id: IDS.dataRequest,
        status: applied ? 'applied' : 'submitted',
        closed_reason: null,
        apply_blocked_reason: null,
        auto_apply: false,
        message: `Please confirm the recycled content of the cathode lot used in pack ${PACK_SERIAL}.`,
        due_at: clock.ahead({ days: 5 }).iso,
        revision: 0,
        passport: {
            snowflake: IDS.pack,
            label: PACK_SERIAL,
            ontology_id: IDS.ontology,
            ontology_version: ONTOLOGY.version,
            default_language: ONTOLOGY.default_language,
        },
        // A request made on its own, not one row of a bulk set.
        set: null,
        supplier: { id: IDS.supplier, name: KESTREL.supplier, status: 'linked' },
        customer_team: TEAMS.kestrel,
        supplier_team: TEAMS.supplier,
        requested_by: ACTORS.quality,
        submitted_by: ACTORS.supplierDesk,
        reviewed_by: acceptedCount > 0 ? ACTORS.quality : null,
        applied_by: applied ? ACTORS.quality : null,
        cancelled_by: null,
        source_passport: null,
        counts: {
            total: REQUESTED_KEYS.length,
            accepted: acceptedCount,
            rejected: 0,
            applied: applied ? REQUESTED_KEYS.length : 0,
        },
        values: entries,
        created_at: created.iso,
        submitted_at: submitted.iso,
        reviewed_at: acceptedCount > 0 ? reviewed.iso : null,
        applied_at: applied ? appliedAt.iso : null,
        cancelled_at: null,
        updated_at: (applied ? appliedAt : acceptedCount > 0 ? reviewed : submitted).iso,
        viewer_role: 'customer',
        abilities: {
            can_submit: false,
            can_review: !applied,
            can_apply: !applied,
            can_cancel: !applied,
            apply_blocked_reason: null,
        },
    };
}

// ─── Import ────────────────────────────────────────────────────────────────

const PACK_LEVEL = ONTOLOGY.data[LEVELS.pack];
const CELL_LEVEL = ONTOLOGY.data[LEVELS.cell];

/** Packs in the imported batch, and the cells each one holds. */
export const IMPORT_PACKS = 48;
export const CELLS_PER_PACK = 768;

/**
 * The column mapping step of the batch import, as MappingGrid takes it: one
 * sheet with a row per cell and its pack's columns repeated (the flat
 * multi-level layout), the combined pack + cell property list grouped the way
 * ImportWizard builds it, every named column autoselected and the operator's
 * note column left unmapped.
 */
export const IMPORT_MAPPING = {
    header: ['Pack serial', 'Date of manufacture', 'Rated capacity (kWh)', 'Cell serial', 'Module position', 'Cell voltage (V)', 'Operator note'],
    properties: [
        { id: 'friendly_id', name: 'Company Assigned ID', type: 'short_text', _group: PACK_LEVEL.name },
        ...PACK_LEVEL.properties.map((property) => ({ ...property, _group: PACK_LEVEL.name })),
        { id: 'friendly_id_child', name: 'Company Assigned ID', type: 'short_text', _group: `${CELL_LEVEL.name} (child)` },
        ...CELL_LEVEL.properties.map((property) => ({ ...property, _group: `${CELL_LEVEL.name} (child)` })),
    ],
    mappedValues: {
        0: 'friendly_id',
        1: P.manufactured,
        2: P.ratedCapacity,
        3: 'friendly_id_child',
        4: P.modulePosition,
        5: P.cellVoltage,
        6: 'none',
    },
    autoColumns: [0, 1, 2, 3, 4, 5],
    showFriendlyId: false,
    // The hero pack's rows, its date of manufacture the one every fixture shows (client R-2).
    examples: [
        [PACK_SERIAL, PACK_MANUFACTURED, '75.2', 'K46-2608-31421', 'M01-P01', '3.614', ''],
        [PACK_SERIAL, PACK_MANUFACTURED, '75.2', 'K46-2608-31422', 'M01-P02', '3.615', ''],
        [PACK_SERIAL, PACK_MANUFACTURED, '75.2', 'K46-2608-31423', 'M01-P03', '3.613', 'Rework tab weld'],
    ],
};

/**
 * The failed-rows header in the rows endpoint's shape (ImportController::
 * buildHeader): one `{ index, label, property_id }` per sheet column, labelled
 * by what it maps to. FailedRowsTable keys and titles its columns by these.
 * The endpoint names only the import's own level; the cell columns here are
 * named from the cell level too, as a reader would expect.
 */
const IMPORT_HEADER = IMPORT_MAPPING.header.map((_, index) => {
    const target = IMPORT_MAPPING.mappedValues[index];
    if (target === 'friendly_id' || target === 'friendly_id_child') {
        return { index, label: 'Friendly ID', property_id: target };
    }
    if (target && target !== 'none') {
        return { index, label: propertyById(target)?.name ?? target, property_id: target };
    }

    return { index, label: `Column ${index + 1}`, property_id: null };
});

/** When the batch's import started: six minutes before it created the pack. */
const IMPORT_STARTED_AT = new Date(Date.parse(PACK_CREATED_AT) - 6 * 60 * 1000).toISOString();

/**
 * The import's detail (ImportDetailBody's props) at `progress` (0 to 1) of
 * the run: every pack first, then the cells, both counting up to their
 * totals. At 1 the run is complete with nothing failed; below it, importing.
 */
export function importDetail(clock, { progress = 1 } = {}) {
    const share = Math.max(0, Math.min(1, progress));
    const cellsTotal = IMPORT_PACKS * CELLS_PER_PACK;
    // Packs are written before their cells: the pack phase finishes in the
    // first tenth of the run, the cells take the rest.
    const packsDone = Math.round(IMPORT_PACKS * Math.min(1, share * 10));
    const cellsDone = Math.round(cellsTotal * Math.max(0, (share - 0.1) / 0.9));
    const complete = share >= 1;
    // It finished as it created the pack (PACK_CREATED_AT), six minutes after it started.
    const created = clock.at(IMPORT_STARTED_AT);

    return {
        importId: IDS.import,
        detail: {
            status: complete ? 'complete' : 'importing',
            total_rows: cellsTotal,
            processed_rows: cellsDone,
            failed_rows: 0,
            failed_infra_rows: 0,
            failed_validation_rows: 0,
            dispatched_rows: cellsTotal,
            phases: [
                { name: PACK_LEVEL.name, depth: LEVELS.pack, total: IMPORT_PACKS, done: packsDone, status: packsDone >= IMPORT_PACKS ? 'complete' : 'importing' },
                { name: CELL_LEVEL.name, depth: LEVELS.cell, total: cellsTotal, done: cellsDone, status: complete ? 'complete' : share > 0.1 ? 'importing' : 'pending' },
            ],
            data: { fileName: 'EVP750_GOT-L2-2609-A_cells.xlsx' },
            created_at: created.iso,
            completed_at: complete ? clock.at(PACK_CREATED_AT).iso : null,
            run_seconds: complete ? 348 : null,
        },
        rows: [],
        header: IMPORT_HEADER,
        mappedValues: IMPORT_MAPPING.mappedValues,
        hasMoreRows: false,
    };
}

// ─── Lab ───────────────────────────────────────────────────────────────────

/** /api/lab/meta for a team with the one ontology. */
export const LAB_META = {
    ontologies: [{
        ontology_id: IDS.ontology,
        name: ONTOLOGY.name,
        depths: Object.values(ONTOLOGY.data).map((level) => ({
            depth: level.id,
            name: level.name,
            properties: level.properties
                .filter((property) => property.type !== 'category')
                .map((property) => ({
                    id: property.id,
                    name: property.name,
                    type: property.type,
                    ...(property.value?.measurement ? { unit: property.value.measurement } : {}),
                })),
        })),
    }],
};

/** A grouped PassQL query over every pack unit, one line per stage. */
export const LAB_QUERY = [
    `from ontology:"${ONTOLOGY.name}" depth:${LEVELS.pack}`,
    '| group by p"Manufacturing plant"',
    '| agg count() as n, avg(p"State of health") as avg_soh',
    '| sort n desc',
].join('\n');

/** What LAB_QUERY returns (mode `groups`): AggregateChart and ResultsTable read it. */
export const LAB_GROUPS = {
    mode: 'groups',
    columns: [
        { key: 'k0', label: 'Manufacturing plant', kind: 'group' },
        { key: 'n', label: 'n', kind: 'aggregate' },
        { key: 'avg_soh', label: 'avg_soh', kind: 'aggregate' },
    ],
    rows: [
        { k0: 'Göteborg Gigafactory, SE', n: 1152, avg_soh: 99.62 },
        { k0: 'Salzgitter Module Works, DE', n: 768, avg_soh: 99.58 },
        { k0: 'Reno Pack Assembly, US', n: 384, avg_soh: 99.66 },
    ],
    duration_ms: 42,
};

/**
 * Today's pack: EVP750-2609-0152 of batch GOT-L2-2609-B, built this morning
 * at the same plant, new (the pack Home's genealogy story imports cells into;
 * fixtures/kestrelGenealogy.js). Its id follows the Kestrel scheme: minted as
 * its passport was created, its sequence its number.
 */
export const TODAYS_PACK = Object.freeze({
    serial: 'EVP750-2609-0152',
    batch: 'GOT-L2-2609-B',
    snowflake: snowflakeAt('pass', '2026-09-29T08:52:10Z', 152),
    stateOfHealth: 100,
});

/**
 * A row query over the model's healthy packs, newest serials first: today's
 * pack, then batch GOT-L2-2609-A's last six (the hero's among them).
 */
export const LAB_ROWS_QUERY = [
    `from ontology:"${ONTOLOGY.name}" depth:${LEVELS.pack}`,
    '| where p"State of health" >= 99.5',
    '| select friendly_id, p"Manufacturing plant", p"State of health"',
    '| sort friendly_id desc',
    '| limit 7',
].join('\n');

const LAB_ROW_TABLE = [
    [147, 99.7], [146, 99.8], [145, 99.5], [144, 99.7], [143, 99.6], [142, 99.8],
];

/** What LAB_ROWS_QUERY returns (mode `rows`). */
export const LAB_ROWS = {
    mode: 'rows',
    columns: [
        { key: 'c0', label: 'friendly_id', kind: 'dim' },
        { key: 'c1', label: 'Manufacturing plant', kind: 'prop' },
        { key: 'c2', label: 'State of health', kind: 'prop' },
    ],
    rows: [
        { c0: TODAYS_PACK.serial, c1: 'Göteborg Gigafactory, SE', c2: TODAYS_PACK.stateOfHealth, __sf: TODAYS_PACK.snowflake },
        ...LAB_ROW_TABLE.map(([number, soh]) => ({
            c0: `EVP750-2609-${String(number).padStart(4, '0')}`,
            c1: 'Göteborg Gigafactory, SE',
            c2: soh,
            __sf: snowflakeAt('pass', '2026-09-08T10:12:30Z', number),
        })),
    ],
    duration_ms: 18,
};
